import React, { useState, useEffect } from "react";
import MainCalculatorsList from "../FoodCalculator/MainCalculators";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
import SharedHelmet from "../Helmet";
import imgUrl from "../../../images/pizza.webp";

const TITLE = "Calculatrice Pizza - Calculez Quantité Pâte et Ingrédients";
const DESCRIPTION =
  "Calculatrice Pizza - Calculez Quantité Pâte et Ingrédients pour adapter vos recettes, portions et conversions en cuisine. Resultat rapide et pratique pour cuisiner avec plus de precision chaque jour.";
const KEYWORDS =
  "pizza quantité pâte et ingrédients, calculatrice pizza quantité pâte et ingrédients, calcul en ligne, cuisine, recette, conversion, resultat rapide";
const OG_URL = "https://do-calculate.com/calculator/fr/pizza/";

const SCHEMA = [
    {
        "@context": "https://schema.org",
        "@type": "WebApplication",
        name: "Calculatrice Pizza",
        description: DESCRIPTION,
        url: OG_URL,
        applicationCategory: "UtilityApplication",
        operatingSystem: "Any",
        offers: {
            "@type": "Offer",
            price: "0",
            priceCurrency: "USD",
        },
    },
];

// Pizza size presets (diameter in cm)
const PIZZA_SIZES = {
    small: { name: "Petite", diameter: 20 },
    medium: { name: "Moyenne", diameter: 25 },
    large: { name: "Grande", diameter: 30 },
    xlarge: { name: "Très Grande", diameter: 35 },
    custom: { name: "Personnalisée", diameter: 0 },
};

// Dough style multipliers
const DOUGH_STYLES = {
    thin: { name: "Pâte Mince (Italienne)", factor: 0.85 },
    regular: { name: "Pâte Régulière (Classique)", factor: 1.0 },
    thick: { name: "Pâte Épaisse (Américaine)", factor: 1.3 },
    deepdish: { name: "Pâte Profonde (Deep Dish)", factor: 1.6 },
};

// Base dough weight per cm² of pizza surface (grams)
const BASE_DOUGH_PER_CM2 = 0.35;

function PizzaCalculator() {
    const [sizePreset, setSizePreset] = useState("large");
    const [customDiameter, setCustomDiameter] = useState("");
    const [doughStyle, setDoughStyle] = useState("regular");
    const [numberOfPizzas, setNumberOfPizzas] = useState(1);
    const [hydration, setHydration] = useState(65);
    const [saltPercent, setSaltPercent] = useState(2);
    const [yeastPercent, setYeastPercent] = useState(0.3);
    const [result, setResult] = useState(null);
    const [error, setError] = useState("");

    const getDiameter = () => {
        if (sizePreset === "custom") {
            return parseFloat(customDiameter || 0);
        }
        return PIZZA_SIZES[sizePreset].diameter;
    };

    const calculatePizza = () => {
        try {
            setError("");
            const diameter = getDiameter();

            if (!diameter || diameter <= 0) {
                setError("Veuillez entrer un diamètre valide pour la pizza");
                setResult(null);
                return;
            }
            if (numberOfPizzas < 1) {
                setError("Veuillez entrer un nombre valide de pizzas");
                setResult(null);
                return;
            }

            const styleFactor = DOUGH_STYLES[doughStyle].factor;
            const radius = diameter / 2;
            const areaCm2 = Math.PI * radius * radius;

            const doughWeightOnePizza = areaCm2 * BASE_DOUGH_PER_CM2 * styleFactor;
            const totalDoughWeight = doughWeightOnePizza * numberOfPizzas;

            const hydrationFrac = hydration / 100;
            const saltFrac = saltPercent / 100;
            const yeastFrac = yeastPercent / 100;
            const divisor = 1 + hydrationFrac + saltFrac + yeastFrac;

            const flourTotal = totalDoughWeight / divisor;
            const waterTotal = flourTotal * hydrationFrac;
            const saltTotal = flourTotal * saltFrac;
            const yeastTotal = flourTotal * yeastFrac;

            const saucePerPizza = areaCm2 * 0.35;
            const totalSauce = saucePerPizza * numberOfPizzas;

            const cheesePerPizza = areaCm2 * 0.5;
            const totalCheese = cheesePerPizza * numberOfPizzas;

            const totalSlices = numberOfPizzas * 8;

            setResult({
                diameter,
                areaCm2,
                doughWeightOnePizza,
                totalDoughWeight,
                flourTotal,
                waterTotal,
                saltTotal,
                yeastTotal,
                saucePerPizza,
                totalSauce,
                cheesePerPizza,
                totalCheese,
                totalSlices,
                styleName: DOUGH_STYLES[doughStyle].name,
                sizeName: sizePreset === "custom" ? `Personnalisée (${diameter} cm)` : `${PIZZA_SIZES[sizePreset].name} (${diameter} cm)`,
            });
        } catch (err) {
            setError("Erreur Calcul: " + err.message);
            setResult(null);
        }
    };

    useEffect(() => {
        calculatePizza();
    }, [sizePreset, customDiameter, doughStyle, numberOfPizzas, hydration, saltPercent, yeastPercent]);

    const handleClear = () => {
        setSizePreset("large");
        setCustomDiameter("");
        setDoughStyle("regular");
        setNumberOfPizzas(1);
        setHydration(65);
        setSaltPercent(2);
        setYeastPercent(0.3);
        setResult(null);
        setError("");
    };

    return (
        <div className="ltr" style={{ direction: "ltr" }}>
            <SharedHelmet
                TITLE={TITLE}
                DESCRIPTION={DESCRIPTION}
                KEYWORDS={KEYWORDS}
                OG_URL={OG_URL}
                SCHEMA={SCHEMA}
                IMAGE={imgUrl}
            />
            <div className="row">
                <div className="col-12 col-md-8">
                    <FirstAds />
                </div>
                <div className="col-12 col-md-4 text-center">
                    <img
                        src={imgUrl}
                        alt="Calculatrice Pizza - Connaître les Quantités d'Ingrédients avec Précision"
                        width="380px"
                        height="300px"
                    />
                </div>
            </div>
            <div className="row">
                <div className="col-12 col-md-8">
                    <br />
                    <div
                        className="bg-success text-white text-center"
                        style={{ padding: "15px", borderRadius: "5px" }}
                    >
                        <h1 style={{ fontSize: "22px", margin: 0 }}>Calculatrice Pizza 🍕</h1>
                    </div>
                    <div className="border border-light mt-3 p-3 bg-white shadow-sm">
                        <div className="mb-4">
                            <p style={{ fontSize: "16px", lineHeight: "1.8" }}>
                                <strong>Calculatrice Pizza</strong> est un outil gratuit et précis qui vous aide à{" "}
                                <strong>calculer les quantités de pâte et d'ingrédients</strong> pour n'importe quel nombre de
                                pizzas à n'importe quelle taille. Entrez le diamètre de la pizza et le nombre désiré
                                pour obtenir une formule précise immédiatement. Idéale pour les restaurants, boulangeries, cuisines domestiques et préparation de fêtes.
                            </p>
                        </div>
                        <hr />
                        <MobileUnderCalAds />
                        <div className="calculatorStyle mb-4">
                            <div className="p-3 mb-3 bg-success text-white text-center rounded">
                                <h2 style={{ fontSize: "18px", margin: 0 }}>
                                    Calcul Ingrédients Pizza Facilement
                                </h2>
                            </div>

                            <div className="row">
                                <div className="col-sm-12 col-md-6">
                                    <label htmlFor="sizePreset">Taille Pizza</label>
                                    <select
                                        id="sizePreset"
                                        className="form-select"
                                        value={sizePreset}
                                        onChange={(e) => setSizePreset(e.target.value)}
                                    >
                                        {Object.entries(PIZZA_SIZES).map(([key, { name, diameter }]) => (
                                            <option key={key} value={key}>
                                                {name}{key !== "custom" ? ` — diamètre ${diameter} cm` : ""}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-sm-12 col-md-6">
                                    <label htmlFor="doughStyle">Type Pâte</label>
                                    <select
                                        id="doughStyle"
                                        className="form-select"
                                        value={doughStyle}
                                        onChange={(e) => setDoughStyle(e.target.value)}
                                    >
                                        {Object.entries(DOUGH_STYLES).map(([key, { name }]) => (
                                            <option key={key} value={key}>
                                                {name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {sizePreset === "custom" && (
                                <div className="row mt-3">
                                    <div className="col-sm-12">
                                        <label htmlFor="customDiameter">Diamètre Pizza Personnalisé (cm)</label>
                                        <input
                                            id="customDiameter"
                                            type="number"
                                            className="form-control"
                                            value={customDiameter}
                                            onChange={(e) => setCustomDiameter(e.target.value)}
                                            min="10"
                                            max="100"
                                            step="0.5"
                                            placeholder="Exemple : 28"
                                            style={{ backgroundColor: "#fff3cd" }}
                                        />
                                    </div>
                                </div>
                            )}

                            <div className="row mt-3">
                                <div className="col-sm-12 col-md-6">
                                    <label htmlFor="numberOfPizzas">Nombre Pizzas</label>
                                    <input
                                        id="numberOfPizzas"
                                        type="number"
                                        className="form-control"
                                        value={numberOfPizzas}
                                        onChange={(e) => setNumberOfPizzas(parseInt(e.target.value) || 1)}
                                        min="1"
                                        max="100"
                                        placeholder="Nombre de pizzas"
                                    />
                                </div>
                                <div className="col-sm-12 col-md-6">
                                    <label htmlFor="hydration">Hydration Pâte (%)</label>
                                    <input
                                        id="hydration"
                                        type="number"
                                        className="form-control"
                                        value={hydration}
                                        onChange={(e) => setHydration(parseFloat(e.target.value) || 65)}
                                        min="50"
                                        max="90"
                                        step="1"
                                        placeholder="65"
                                    />
                                </div>
                            </div>

                            <div className="row mt-3">
                                <div className="col-sm-12 col-md-4">
                                    <label>Pourcentage Sel (%)</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        value={saltPercent}
                                        onChange={(e) => setSaltPercent(parseFloat(e.target.value) || 2)}
                                        min="0"
                                        max="5"
                                        step="0.1"
                                        placeholder="2"
                                    />
                                </div>
                                <div className="col-sm-12 col-md-4">
                                    <label>Pourcentage Levure (%)</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        value={yeastPercent}
                                        onChange={(e) => setYeastPercent(parseFloat(e.target.value) || 0.3)}
                                        min="0.1"
                                        max="3"
                                        step="0.1"
                                        placeholder="0.3"
                                    />
                                </div>
                            </div>

                            <div className="text-center mb-3 mt-4">
                                <button
                                    onClick={handleClear}
                                    className="btn btn-secondary"
                                    style={{ fontSize: "16px", padding: "10px 30px" }}
                                >
                                    Effacer
                                </button>
                            </div>

                            {error && (
                                <div className="alert alert-warning" role="alert">
                                    <strong>Alerte :</strong> {error}
                                </div>
                            )}

                            {result && (
                                <div className="mt-4">
                                    <h3 style={{ fontSize: "18px", marginBottom: "15px", textAlign: "center" }}>
                                        <strong>Résultat :</strong>
                                    </h3>

                                    <div className="mb-3">
                                        <table className="table bmi-table" style={{ width: "100%" }}>
                                            <thead>
                                                <tr>
                                                    <th className="bmi-header" style={{ textAlign: "center" }}>Description</th>
                                                    <th className="bmi-header" style={{ textAlign: "center" }}>Valeur</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td style={{ textAlign: "center" }}>Poids Pâte (1 pizza)</td>
                                                    <td style={{ textAlign: "center", fontSize: "18px", fontWeight: "bold", color: "#1a5f3f" }}>
                                                        {result.doughWeightOnePizza.toFixed(0)} g
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td style={{ textAlign: "center" }}>Pâte Totale ({numberOfPizzas} pizzas)</td>
                                                    <td style={{ textAlign: "center", fontSize: "18px", fontWeight: "bold", color: "#0f3d28" }}>
                                                        {result.totalDoughWeight.toFixed(0)} g
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td style={{ textAlign: "center" }}>Farine</td>
                                                    <td style={{ textAlign: "center", fontSize: "16px" }}>{result.flourTotal.toFixed(0)} g</td>
                                                </tr>
                                                <tr>
                                                    <td style={{ textAlign: "center" }}>Eau</td>
                                                    <td style={{ textAlign: "center", fontSize: "16px" }}>{result.waterTotal.toFixed(0)} ml</td>
                                                </tr>
                                                <tr>
                                                    <td style={{ textAlign: "center" }}>Sel</td>
                                                    <td style={{ textAlign: "center", fontSize: "16px" }}>{result.saltTotal.toFixed(1)} g</td>
                                                </tr>
                                                <tr>
                                                    <td style={{ textAlign: "center" }}>Levure</td>
                                                    <td style={{ textAlign: "center", fontSize: "16px" }}>{result.yeastTotal.toFixed(1)} g</td>
                                                </tr>
                                                <tr>
                                                    <td style={{ textAlign: "center" }}>Sauce (par pizza)</td>
                                                    <td style={{ textAlign: "center", fontSize: "16px" }}>{result.saucePerPizza.toFixed(0)} g</td>
                                                </tr>
                                                <tr>
                                                    <td style={{ textAlign: "center" }}>Fromage Mozzarella (par pizza)</td>
                                                    <td style={{ textAlign: "center", fontSize: "16px" }}>{result.cheesePerPizza.toFixed(0)} g</td>
                                                </tr>
                                                <tr>
                                                    <td style={{ textAlign: "center" }}>Total Tranches (8 par pizza)</td>
                                                    <td style={{ textAlign: "center", fontSize: "16px" }}>{result.totalSlices}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>

                                    <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header"><h3 style={{ fontSize: "16px" }}>Conseils d'utilisation</h3></div>
              <div className="info-section-body">
                <p>Pour obtenir un resultat pertinent, verifiez vos unites, saisissez des valeurs realistes et utilisez ce calcul comme aide a la decision, puis adaptez selon votre situation.</p>
              </div>
            </div>
                                        <div className="info-section">
                                            <div className="info-section-header"><strong>Résumé Recette</strong></div>
                                            <div className="info-section-body">
                                                <ul style={{ listStyle: "none", padding: 0, lineHeight: "2" }}>
                                                    <li>🍕 <strong>Taille :</strong> {result.sizeName}</li>
                                                    <li>🍞 <strong>Type Pâte :</strong> {result.styleName}</li>
                                                    <li>📐 <strong>Surface Pizza :</strong> {result.areaCm2.toFixed(0)} cm²</li>
                                                    <li>🧂 <strong>Sauce Totale ({numberOfPizzas} pizzas) :</strong> {result.totalSauce.toFixed(0)} g</li>
                                                    <li>🧀 <strong>Fromage Total ({numberOfPizzas} pizzas) :</strong> {result.totalCheese.toFixed(0)} g</li>
                                                </ul>
                                                <div className="alert alert-info mt-3" style={{ fontSize: "14px" }}>
                                                    <strong>💡 Astuce :</strong> Hydration utilisée : {hydration}% — Un bon niveau pour pizza maison est entre 60-70%.
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        <hr />
                        <div className="row">
                            <RelatedLinks />
                        </div>
                    </div>
                </div>
                <div className="col-sm-12 col-md-4">
                    <SecondAds />
                    <br />
                    <MainCalculatorsList />
                </div>
            </div>
            <div className="row" style={{ marginTop: "20px" }}>
                <div className="col-12 col-md-8">
                    <div className="info-sections-container">
                        <div className="info-section">
                            <div className="info-section-header">
                                <h3 style={{ fontSize: "16px" }}>Comment faire une bonne pizza maison</h3>
                            </div>
                            <div className="info-section-body">
                                <p>
                                    La pizza maison commence par une bonne pâte. Le ratio hydratation — c'est-à-dire la quantité d'eau par rapport à la farine —
                                    est le facteur le plus important. Entre 60 et 65 % d'hydratation, vous obtenez une pâte facile à travailler avec une croûte légèrement croustillante.
                                    Entre 70 et 75 %, la pâte est plus souple et donne une croûte aérée et moelleuse, comme la pizza napolitaine. Cette calculatrice vous aide
                                    à ajuster ces quantités selon la taille et le nombre de pizzas que vous voulez préparer.
                                </p>
                                <ul>
                                    <li>Pâte fine : hydratation 58-62%, cuisson à haute température (250°C+)</li>
                                    <li>Pâte napolitaine : hydratation 65-70%, bords gonflés et mâcheux</li>
                                    <li>Pâte épaisse (style américain) : hydratation 70-75%, moelleuse à l'intérieur</li>
                                    <li>Pâte croustillante : proportion d'huile plus élevée, cuisson longue à 200°C</li>
                                </ul>
                            </div>
                        </div>
                        <div className="info-section">
                            <div className="info-section-header">
                                <h3 style={{ fontSize: "16px" }}>Quantités de sauce et fromage selon la taille</h3>
                            </div>
                            <div className="info-section-body">
                                <p>
                                    Les Italiens sont très précis sur les quantités : une pizza napolitaine de 30 cm utilise environ 70-80 g de sauce tomate et 80-100 g de
                                    mozzarella. Mettre trop de garniture rend la pâte détrempée et empêche une bonne cuisson. La règle générale est de couvrir la surface
                                    sans la noyer — laissez de l'espace sur les bords et étalez la sauce en couche fine.
                                </p>
                                <ul>
                                    <li>Pizza 20 cm : 45-55 g sauce, 60-70 g fromage</li>
                                    <li>Pizza 28 cm : 70-85 g sauce, 90-110 g fromage</li>
                                    <li>Pizza 35 cm : 110-130 g sauce, 140-170 g fromage</li>
                                    <li>Pizza 40 cm : 145-165 g sauce, 180-210 g fromage</li>
                                </ul>
                            </div>
                        </div>
                        <div className="info-section">
                            <div className="info-section-header">
                                <h3 style={{ fontSize: "16px" }}>Questions fréquentes sur la pizza</h3>
                            </div>
                            <div className="info-section-body">
                                <p><strong>Combien de temps repose la pâte ?</strong> Au minimum 1 heure à température ambiante, ou idéalement 24 à 72 heures au réfrigérateur. Une longue fermentation développe plus de saveur.</p>
                                <p><strong>Peut-on congeler la pâte ?</strong> Oui, après la première levée. Décongelez au réfrigérateur pendant 12 heures avant utilisation.</p>
                                <p><strong>Quelle farine utiliser ?</strong> La farine de type 00 (italienne) est idéale pour la pizza — elle est très fine et riche en gluten, ce qui donne une pâte élastique et légère.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default PizzaCalculator;
